import type { Entity, EntityType } from '@platform/entities/entity.class';
import type { ColumnParams, DataType, RpcFilters, RpcTable, SerializableParameter } from '@platform/shared/rpc';
import { NodeAPI } from '@platform/shared/rpc';
import type { IRequestOptions, ReferenceDataQueryDto } from '@platform/shared/rpc/node/api.gen';
import { RpcService } from '@platform/shared/rpc/node/api.gen';
import type { AxiosRequestConfig } from 'axios';
import { roleRequestState } from '@platform/shared/lib/roles';

export type RpcJoin = ({ ref: string } | { lateral: string } | { subQuery: string }) & {
    type?: string;
    on?: string;
    alias?: string;
    include: (string | [string, string] | { raw: string; as?: string })[];
};
export type RpcOrderType = 'ASC' | 'DESC';

export type RpcOrder = Array<[string, RpcOrderType | `${RpcOrderType} NULLS ${'FIRST' | 'LAST'}`] | string>;

export type RpcFnArgTemplate =
    | 'ROLE_EMPLOYEE_IDS'
    | 'USER_EMPLOYEE_ID'
    | 'USER_SYS_USER_ID'
    | 'USER_EXT_USER_ID'
    | 'USER_IS_SUPERADMIN'
    | 'USER_PERSON_ID'
    | 'ORG_ID'
    | 'SERVICE_ID'
    | 'SYS_SERVICE_ORG_ID';
export type RpcFnArg =
    | string
    | {
          value: any;
          type?: string;
      }
    | { useEnv: RpcFnArgTemplate }
    | { raw: string; parameters?: SerializableParameter[] };

export type LegacyOptions = {
    dateAsEpoch?: boolean;
    timestampAsEpoch?: boolean;
    alwaysPopulateRefs?: boolean;
    doNotParseJson?: boolean;
};
type LegacyParameter = undefined | boolean | LegacyOptions;

export type ICallFn = {
    fn: string;
    args: RpcFnArg[] | Record<string, RpcFnArg> | string;
    struct: Record<string, undefined | ({ dataType: DataType | [DataType] } & ColumnParams)>;
    options?: {
        noFilterByOrg?: boolean;
        anyOrganization?: boolean;
        joins?: RpcJoin[];
        filters?: RpcFilters;
        sort?: RpcOrder;
        withFilesInfo?: boolean;
        withAccessRight?: boolean;
        withViewedInfo?: boolean;
        rowStatuses?: Array<'ACT' | 'DRF' | 'DEL' | 'REC' | string> | false;
        groupBy?: string | { mode: 'except'; columns: string[] };
        legacy?: LegacyParameter;
        epochDateTime?: boolean;
        isProcedure?: boolean;
        page?: number | [page: number, pageSize?: number] | { offset: number; limit?: number };
        passSysServiceOrgLinkId?: boolean;
    };
};

const legacy: LegacyOptions = {
    dateAsEpoch: false,
    timestampAsEpoch: true,
    alwaysPopulateRefs: false,
};
type NodeRpcAdditionalOptions = {
    forceReadStructureFromDB?: boolean;
};

const handleRequestScope = (requestOptions?: IRequestOptions): IRequestOptions => {
    const headers: AxiosRequestConfig['headers'] = { ...requestOptions?.headers };
    if (roleRequestState.current) {
        headers['X-Request-Scope'] = roleRequestState.current;
        headers['X-Request-Scope-Desc'] = roleRequestState.description;
        roleRequestState.current = null;
        roleRequestState.description = null;
    }
    return { ...requestOptions, headers };
};

export default {
    callFunction: (body: ICallFn, requestOptions?: IRequestOptions & { count?: boolean }) => {
        return RpcService.callFunction(
            {
                body: { ...body, options: { epochDateTime: false, ...body.options, legacy } },
                count: requestOptions?.count ? 'true' : undefined,
            } as any,
            handleRequestScope(requestOptions)
        );
    },
    readWithRefs: <T extends typeof Entity>(
        entity:
            | T
            | {
                  tableInfo: RpcTable;
                  struct: Record<string, undefined | ({ dataType: DataType | [DataType] } & ColumnParams)>;
              }
            | string,
        options?: ICallFn['options'] & NodeRpcAdditionalOptions,
        requestOptions?: IRequestOptions
    ): Promise<EntityType<InstanceType<T>>[]> => {
        return RpcService.readWithRefs(
            {
                body: {
                    entity: (typeof entity === 'string'
                        ? entity
                        : {
                              struct: '_struct' in entity ? entity._struct : entity.struct,
                              tableInfo: '_tableInfo' in entity ? entity._tableInfo : entity.tableInfo,
                          }) as any,
                    options: { epochDateTime: false, legacy, ...options },
                },
            },
            handleRequestScope(requestOptions)
        ) as Promise<EntityType<InstanceType<T>>[]>;
    },

    modify: <T extends typeof Entity>(
        entity:
            | T
            | { tableInfo: RpcTable; struct: Record<string, { dataType: DataType | [DataType] } & ColumnParams> }
            | string,
        data: EntityType<InstanceType<T>>[] | EntityType<InstanceType<T>>,
        options: {
            onConflict?: 'update' | 'nothing';
            tzUtc?: boolean;
            allowUndefined?: boolean;
            legacy?: boolean;
            updateColumns?: string[];
        } & NodeRpcAdditionalOptions = {},
        requestOptions?: IRequestOptions
    ) =>
        RpcService.modify(
            {
                body: {
                    entity: (typeof entity === 'string'
                        ? entity
                        : {
                              struct: '_struct' in entity ? entity._struct : entity.struct,
                              tableInfo: '_tableInfo' in entity ? entity._tableInfo : entity.tableInfo,
                          }) as any,
                    data: Array.isArray(data) ? data : [data],
                    options,
                },
            },
            requestOptions
        ),
    deletePermanently: (tableInfo: Pick<RpcTable, 'tableName' | 'schemaName'>, ids: string[]) =>
        RpcService.deletePermanently({ body: { tableInfo, ids } }),

    markAsDeleted: (tableInfo: Pick<RpcTable, 'tableName' | 'schemaName'>, ids: string[]) =>
        RpcService.markAsDeleted({ body: { tableInfo, ids } }),

    readReferenceDataLegacy: (params: Partial<ReferenceDataQueryDto> & { fieldNameSort?: string }) =>
        NodeAPI.ReferenceDataService.getReferenceData({
            body: params as ReferenceDataQueryDto,
        })
            .then((data) => ({ success: true, data }))
            .catch((error) => ({ success: false, error, data: null })),
};
